import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Moment from 'react-moment';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import {
  Ban,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CircleMinus,
  Clock3,
  Mail,
  Pencil,
  Phone,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import { Routes } from '../../../../../config/routes';
import { Urls } from '../../../../../config/urls';
import l from '../../../../../lang';
import { useDashboard } from '../../../../../store/dashboard';
import { usePayouts } from '../../../../../store/payouts';
import { PayoutContactBankAccount } from '../../../../../store/payouts/interfaces';
import { createDate, getMappedCountryCode } from '../../../../../utils';
import { countryName } from '../../../../../utils/payment-methods-utils';
import { ReactComponent as BackIcon } from '../../../../../assets/icons/back.svg';
import ButtonLink from '../../../../common/button-link';
import Card from '../../../../common/card';
import Loading from '../../../../common/loading';
import { Button } from '../../../../common/button';
import ButtonMoreMenu from '../../../../common/button-more-menu';
import SuccessfulAnimation from '../../../../common/successful-animation';
import DeleteAccountDialog from '../delete-account-dialog';
import RevalidateIdentityDialog from '../revalidate-dialog';
import './styles.scss';

const formatEnumValue = (value?: string) => {
  if (!value) {
    return '-';
  }

  return value
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const renderDate = (value?: string) => {
  if (!value) {
    return '-';
  }

  return <Moment format="DD/MM/YYYY">{createDate(value)}</Moment>;
};

const DetailField = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="payout-contact-detail__field">
    <span className="payout-contact-detail__field-label">{label}</span>
    <span className="payout-contact-detail__field-value">{value || '-'}</span>
  </div>
);

const PayoutContactDetail = () => {
  const history = useHistory();
  const { contact_id: contactId } = useParams<{ contact_id: string }>();
  const { merchantMe } = useDashboard();
  const { contactsList, getPayoutContactsList, setPayoutContactBankAccountPreferred } = usePayouts();
  const [hasRequestedContacts, setHasRequestedContacts] = useState(contactsList.data.length > 0);
  const [showRevalidationDialog, setShowRevalidationDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<PayoutContactBankAccount | null>(null);
  const [preferringAccountId, setPreferringAccountId] = useState<number | null>(null);
  const [showPreferredSuccess, setShowPreferredSuccess] = useState(false);
  const { data, loading } = contactsList;
  const onAddBankAccount = () =>
    history.push(Routes.PAYOUT_CONTACT_BANK_ACCOUNT_CREATE.replace(':beneficiary_id', contactId));
  const onEditBankAccount = (account: PayoutContactBankAccount) =>
    history.push(
      Routes.PAYOUT_CONTACT_BANK_ACCOUNT_EDIT.replace(':beneficiary_id', contactId).replace(
        ':account_id',
        String(account.id)
      )
    );

  const contact = useMemo(() => data.find(item => String(item.id) === contactId), [contactId, data]);

  useEffect(() => {
    if (!data.length && merchantMe?.id && !hasRequestedContacts) {
      setHasRequestedContacts(true);
      getPayoutContactsList(String(merchantMe.id));
    }
  }, [data.length, hasRequestedContacts, merchantMe?.id]);

  useEffect(() => {
    if (hasRequestedContacts && !loading && !contact) {
      history.replace(Routes.PAYOUT_CONTACTS);
    }
  }, [contact, hasRequestedContacts, history, loading]);

  if (!contact) {
    return (
      <div className="payout-contact-detail__loading">
        <Loading />
      </div>
    );
  }

  const normalizedStatus = (contact.status || '').toUpperCase();
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || '-';
  const initial = (contact.first_name || '').trim().charAt(0).toUpperCase();
  const countryCode = getMappedCountryCode(contact.country);

  const statusIcon = () => {
    if (normalizedStatus === 'VERIFIED') {
      return <Check size={13} strokeWidth={2.5} aria-hidden />;
    }
    if (normalizedStatus === 'PENDING' || normalizedStatus === 'PROCESSING') {
      return <Clock3 size={13} strokeWidth={2.25} aria-hidden />;
    }
    if (normalizedStatus === 'BLOCKED') {
      return <Ban size={13} strokeWidth={2.25} aria-hidden />;
    }
    if (normalizedStatus === 'REJECTED') {
      return <X size={13} strokeWidth={2.5} aria-hidden />;
    }
    return <CircleMinus size={13} strokeWidth={2.25} aria-hidden />;
  };

  const statusBadge = (
    <span className={`payout-contact-detail__status payout-contact-detail__status--${normalizedStatus.toLowerCase()}`}>
      {statusIcon()}
      <span>{l(`payoutContacts.status.${normalizedStatus}`)}</span>
    </span>
  );

  const handleSetPreferredAccount = (account: PayoutContactBankAccount) => {
    if (!merchantMe?.id || account.preferred || preferringAccountId !== null) {
      return;
    }

    setPreferringAccountId(account.id);
    setPayoutContactBankAccountPreferred(
      String(merchantMe.id),
      String(contact.id),
      String(account.id),
      () => {
        setPreferringAccountId(null);
        getPayoutContactsList(String(merchantMe.id));
        setShowPreferredSuccess(true);
      },
      () => setPreferringAccountId(null)
    );
  };

  return (
    <div className="payout-contact-detail">
      <Card className="payout-contact-detail__shell">
        <div className="payout-contact-detail__toolbar">
          <ButtonLink
            type="primary"
            text={
              <>
                <BackIcon /> {l('payoutContacts.detail.back')}
              </>
            }
            onClick={() => history.push(Routes.PAYOUT_CONTACTS)}
          />
        </div>

        <div className="payout-contact-detail__layout">
          <Card nested className="payout-contact-detail__profile-card">
            <aside className="payout-contact-detail__profile">
              <div className="payout-contact-detail__profile-header">
                <span className="payout-contact-detail__avatar" aria-hidden>
                  {initial}
                </span>
                <h1 className="payout-contact-detail__name">{fullName}</h1>
                {contact.email && (
                  <div className="payout-contact-detail__profile-row">
                    <Mail size={16} strokeWidth={1.75} aria-hidden />
                    <span>{contact.email}</span>
                  </div>
                )}
                {contact.phone && (
                  <div className="payout-contact-detail__profile-row">
                    <Phone size={16} strokeWidth={1.75} aria-hidden />
                    <span>{contact.phone}</span>
                  </div>
                )}
              </div>

              <div className="payout-contact-detail__divider" aria-hidden />

              <div className="payout-contact-detail__profile-details">
                <div className="payout-contact-detail__profile-row">
                  <img
                    alt=""
                    className="payout-contact-detail__country-flag country-flag--rounded"
                    src={Urls.roundedFlags.replace(':code', countryCode)}
                  />
                  <span>{countryName(contact.country)}</span>
                </div>
                <div className="payout-contact-detail__date-row">
                  <CalendarDays size={16} strokeWidth={1.75} aria-hidden />
                  <span>
                    {l('payoutContacts.detail.createdAt')}: {renderDate(contact.created_at)}
                  </span>
                </div>
                <div className="payout-contact-detail__date-row">
                  <CalendarDays size={16} strokeWidth={1.75} aria-hidden />
                  <span>
                    {l('payoutContacts.detail.expiresAt')}: {renderDate(contact.expires_at)}
                  </span>
                </div>
              </div>

              <div className="payout-contact-detail__divider" aria-hidden />
              <div className="payout-contact-detail__profile-status">
                <span>{l('payoutContacts.detail.status')}:</span>
                {statusBadge}
              </div>
            </aside>
          </Card>

          <Card nested className="payout-contact-detail__content-card">
            <main className="payout-contact-detail__content">
              <section className="payout-contact-detail__section">
                <header className="payout-contact-detail__section-header">
                  <UserRound size={18} strokeWidth={1.75} aria-hidden />
                  <h2>{l('payoutContacts.detail.beneficiaryInfo')}</h2>
                </header>
                <div className="payout-contact-detail__info-grid">
                  <DetailField
                    label={l('payoutContacts.detail.identification')}
                    value={
                      <>
                        <strong>{contact.document_type || '-'}</strong>
                        <span>{contact.document || '-'}</span>
                      </>
                    }
                  />
                  <DetailField
                    label={l('payoutContacts.detail.relationship')}
                    value={l(`payoutContacts.relationship.${contact.relationship}`)}
                  />
                  <DetailField
                    label={l('payoutContacts.detail.flowType')}
                    value={l(`payoutContacts.detail.flowType.${contact.flow_type}`)}
                  />
                  <DetailField label={l('payoutContacts.detail.verificationAttempts')} value={contact.total_attempts ?? 0} />
                </div>
              </section>

              <section className="payout-contact-detail__section">
                <header className="payout-contact-detail__section-header payout-contact-detail__section-header--accounts">
                  <div className="payout-contact-detail__section-title">
                    <BriefcaseBusiness size={18} strokeWidth={1.75} aria-hidden />
                    <h2>{l('payoutContacts.detail.bankAccounts')}</h2>
                  </div>
                  <Button variant="outlined" className="payout-contact-detail__add-account" onClick={onAddBankAccount}>
                    + {l('payouts.create.payoutData.addBankAccount')}
                  </Button>
                </header>
                <div className="payout-contact-detail__accounts-wrapper">
                  {contact.bank_accounts.length ? (
                    <table className="payout-contact-detail__accounts">
                      <thead>
                        <tr>
                          <th>{l('payoutContacts.detail.account.preferred')}</th>
                          <th>{l('payoutContacts.detail.account.country')}</th>
                          <th>{l('payoutContacts.detail.account.name')}</th>
                          <th>{l('payoutContacts.detail.account.documentType')}</th>
                          <th>{l('payoutContacts.detail.account.document')}</th>
                          <th>{l('payoutContacts.detail.account.bank')}</th>
                          <th>{l('payoutContacts.detail.account.number')}</th>
                          <th>{l('payoutContacts.detail.account.details')}</th>
                          <th aria-label={l('payoutContacts.detail.account.actions')} />
                        </tr>
                      </thead>
                      <tbody>
                        {contact.bank_accounts.map(account => (
                          <tr key={account.id}>
                            <td>
                              <button
                                type="button"
                                className={`payout-contact-detail__preferred${
                                  account.preferred ? ' payout-contact-detail__preferred--selected' : ''
                                }`}
                                aria-label={l('payoutContacts.detail.account.preferred')}
                                aria-pressed={account.preferred}
                                disabled={account.preferred || preferringAccountId !== null}
                                onClick={() => handleSetPreferredAccount(account)}
                              />
                            </td>
                            <td>
                              <span className="payout-contact-detail__account-country">
                                <img
                                  alt=""
                                  className="country-flag--rounded"
                                  src={Urls.roundedFlags.replace(':code', getMappedCountryCode(account.country))}
                                />
                                <span>{account.currency}</span>
                              </span>
                            </td>
                            <td>{fullName}</td>
                            <td>{contact.document_type || '-'}</td>
                            <td>{contact.document || '-'}</td>
                            <td>{account.bank_name?.trim() || account.bank_code || '-'}</td>
                            <td>{account.bank_account || '-'}</td>
                            <td>
                              <span className="payout-contact-detail__account-details">
                                {formatEnumValue(account.account_type)}
                                {account.bank_branch ? ` · ${account.bank_branch}` : ''}
                              </span>
                            </td>
                            <td className="payout-contact-detail__account-actions">
                              <ButtonMoreMenu
                                id={`payout-contact-account-actions-${account.id}`}
                                items={[
                                  {
                                    content: (
                                      <span className="payout-contact-detail__account-menu-item">
                                        <Pencil size={18} strokeWidth={1.75} aria-hidden />
                                        {l('payoutContacts.detail.account.edit')}
                                      </span>
                                    ),
                                    onClick: () => onEditBankAccount(account),
                                  },
                                  {
                                    content: (
                                      <span className="payout-contact-detail__account-menu-item">
                                        <Trash2 size={18} strokeWidth={1.75} aria-hidden />
                                        {l('payoutContacts.detail.account.delete')}
                                      </span>
                                    ),
                                    onClick: () => setAccountToDelete(account),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="payout-contact-detail__accounts-empty">{l('payoutContacts.detail.account.empty')}</div>
                  )}
                </div>
              </section>

              <section className="payout-contact-detail__section payout-contact-detail__section--verification">
                <header className="payout-contact-detail__section-header">
                  <Check size={18} strokeWidth={1.75} aria-hidden />
                  <h2>{l('payoutContacts.detail.verification')}</h2>
                </header>
                <div className="payout-contact-detail__verification">
                  <div className="payout-contact-detail__verification-info">
                    <DetailField label={l('payoutContacts.detail.status')} value={statusBadge} />
                    <DetailField
                      label={l('payoutContacts.detail.lastVerifiedAt')}
                      value={renderDate(contact.last_verified_at)}
                    />
                  </div>
                  <div className="payout-contact-detail__revalidation">
                    <Button
                      variant="outlined"
                      disabled={!contact.manual_revalidation_eligible}
                      onClick={() => setShowRevalidationDialog(true)}
                    >
                      {l('payoutContacts.menu.revalidate')}
                    </Button>
                    {contact.manual_revalidation_fee_usd != null && (
                      <span>
                        {l('payoutContacts.detail.revalidationFee')}: USD {contact.manual_revalidation_fee_usd}
                      </span>
                    )}
                  </div>
                </div>
              </section>
            </main>
          </Card>
        </div>
      </Card>
      <RevalidateIdentityDialog
        open={showRevalidationDialog}
        contact={contact}
        beneficiaryId={contact.id}
        onClose={() => setShowRevalidationDialog(false)}
      />
      <DeleteAccountDialog
        open={Boolean(accountToDelete)}
        beneficiaryId={contact.id}
        account={accountToDelete}
        onClose={() => setAccountToDelete(null)}
        onDeleted={() => {
          if (merchantMe?.id) {
            getPayoutContactsList(String(merchantMe.id));
          }
        }}
      />
      <Dialog
        open={showPreferredSuccess}
        onClose={() => setShowPreferredSuccess(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ className: 'payout-contact-detail__preferred-success' }}
      >
        <IconButton
          className="payout-contact-detail__preferred-success-close"
          aria-label={l('common.close')}
          onClick={() => setShowPreferredSuccess(false)}
        >
          <X size={20} aria-hidden />
        </IconButton>
        <DialogContent>
          <SuccessfulAnimation text={l('payoutContacts.detail.account.preferred.success')} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PayoutContactDetail;
